import { useContext, useMemo } from 'react'

import { EnvironmentContext } from 'config/environment'
import {
  FALLBACK_LOCALES,
  SUPPORTED_LANGUAGES,
  SUPPORTED_LANGUAGES_LABELS,
  localeLanguagePart,
} from 'config/locales'
import type { Form as FormSchema } from 'factorial-form'

import { observer } from 'mobx-react'

import SelectField from 'modules/core/components/SelectField'
import i18n from 'modules/core/lib/i18n'

import { useLanguageSelectorFeatures } from './useLanguageSelectorFeatures'

interface Props {
  label?: string
  form: FormSchema
}

/**
 * Modern hook that uses GraphQL instead of deprecated hasFeature.
 */
export const useWithoutDisabledLocales = (locales: string[]) => {
  const { developerEnglishEnabled } = useContext(EnvironmentContext)
  const {
    loading: featuresLoading,
    inContextLocalization,
    greekLanguage,
    ngoLocale,
  } = useLanguageSelectorFeatures()

  return useMemo(() => {
    // If features are still loading, use default enabled state to avoid flickering
    const filters = {
      developerEnglishEnabled,
      inContextLocalization: featuresLoading || inContextLocalization,
      greekLanguage: featuresLoading || greekLanguage,
      ngoLocale: featuresLoading || ngoLocale,
    }

    return locales.filter((locale) => {
      switch (locale) {
        case 'en':
          return filters.developerEnglishEnabled
        case 'eo':
          return filters.inContextLocalization
        case 'el':
          return filters.greekLanguage
        case 'fr-ng':
          return filters.ngoLocale
        case 'fr':
          return !filters.ngoLocale
        default:
          return true
      }
    })
  }, [
    locales,
    developerEnglishEnabled,
    featuresLoading,
    inContextLocalization,
    greekLanguage,
    ngoLocale,
  ])
}

const LanguageSelector = ({ label, form }: Props) => {
  const localeField = form.get('locale')
  const currentLocale: string = localeField.value.toLowerCase()
  const languageFromLocale = localeLanguagePart(currentLocale)

  const possibleLanguages = [
    // Current locale (e.g. en-gb)
    currentLocale,
    // Current locale without region code (e.g. pt-pt -> pt)
    languageFromLocale,
    // Default locale for the current language (e.g. en-au -> en-us)
    FALLBACK_LOCALES[languageFromLocale],
  ]

  const supportedLanguages = useWithoutDisabledLocales(SUPPORTED_LANGUAGES)
  const selectedLanguage = possibleLanguages.find(
    (locale) => locale && supportedLanguages.includes(locale)
  )

  // If the language is not found, we show a "Select" option
  const missingLanguage = selectedLanguage ? {} : { none: i18n.t('common.select') }

  const options = supportedLanguages.map((locale) => ({
    value: locale,
    label: SUPPORTED_LANGUAGES_LABELS[locale] || locale,
  }))

  return (
    <SelectField
      label={label || i18n.t('personal_settings.language')}
      options={options}
      value={selectedLanguage}
      onChange={(value) => localeField.set(value)}
      showOriginalLabelText
      mandatory
      {...missingLanguage}
    />
  )
}

export default observer(LanguageSelector)
